import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { ProjectService } from "@/services/project.service";
import type { ProjectMemberService } from "@/services/project-member.service";
import type {
  ProjectActivityService,
  ProjectCommentService,
  ProjectLinkedSubmissionsService,
} from "@/services/projects-v3.service";
import type { WorkItemService } from "@/services/work-item.service";
import type { WorklogService } from "@/services/worklog.service";
import type { WorkspaceJobRoleService } from "@/services/workspace-job-role.service";
import type { TProjectActivityPage, TProjectCommentPage } from "@/types/project";
import { FRESHNESS } from "./policies";
import { scopedKey, scopedRead, type QueryScope } from "./scope";

/**
 * The reads that hang off one project, which the caching pilot moves onto Query
 * (NG-2544, contract §2, R3 to R13 less R11):
 *
 * - **R3** `project.activities` and **R4** `project.comments`: the two
 *   newest-first feeds, one keyset page per fetch. Mutable.
 * - **R5** attachments, **R6** linked submissions, **R7** the worklog summary,
 *   **R8** the cost summary: mutable working data the overview reads.
 * - **R9** `project.members` and **R10** `project.memberMe`: access-sensitive,
 *   because who is on a project decides what they may see and do. Concurrent
 *   reads share a request; once settled, the next mount asks again.
 * - **R12** `project.userProperties`: the signed-in user's work-item display
 *   properties for the project, read on its work-items view. Mutable.
 * - **R13** `project.jobRoleRecommendations`: Smart Assignment suggestions.
 *   Mutable.
 *
 * Every key sits under `[slug, uid, 'project', id, …]`, beneath R1's
 * (`projects.ts`), so a prefix invalidation or removal of `('project', id)`
 * reaches all of them. Each read is a plain idempotent GET, so each declares
 * `"safe-read"` and `runRead` owns its retries.
 *
 * The definitions take the service rather than reaching for the root store, so
 * a store passes its service and a test passes a fake.
 */

/** A key under one project: `[slug, uid, 'project', id, resource, …]`. */
function projectResourceKey<const R extends readonly unknown[]>(
  scope: QueryScope,
  projectId: string,
  resource: string,
  ...representation: R
) {
  return scopedKey(scope, "project", projectId, resource, ...representation);
}

/** The R3 key. */
export function projectActivitiesKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "activities");
}

/** The R4 key. */
export function projectCommentsKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "comments");
}

/** The R5 key. */
export function projectAttachmentsKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "attachments");
}

/** The R6 key. */
export function projectLinkedSubmissionsKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "linked-submissions");
}

/** The R7 key. */
export function projectWorklogSummaryKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "worklogs");
}

/** The R8 key. */
export function projectCostSummaryKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "cost-summary");
}

/**
 * The R9 key. `q` is trimmed because the service drops a blank one, so `""`
 * and `"  "` are the same request and share one entry.
 */
export function projectMembersKey(
  scope: QueryScope,
  projectId: string,
  { q }: { q?: string } = {},
) {
  return projectResourceKey(scope, projectId, "members", { q: q?.trim() ?? "" });
}

/** The R10 key. */
export function projectMemberMeKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "member-me");
}

/** The R12 key. */
export function projectUserPropertiesKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "user-properties");
}

/** The R13 key. */
export function projectJobRoleRecommendationsKey(scope: QueryScope, projectId: string) {
  return projectResourceKey(scope, projectId, "job-role-recommendations");
}

/**
 * The next page of a keyset feed: offered only while the server says there is
 * one and its cursor moved, the same stop rule as `getAllPages`.
 */
function nextFeedCursor(
  last: { next_page_results: boolean; next_cursor: string | null },
  cursor: string | null,
): string | undefined {
  return last.next_page_results && last.next_cursor && last.next_cursor !== cursor
    ? last.next_cursor
    : undefined;
}

/**
 * R3: the project's activity feed, newest first, one page per fetch. The first
 * page is read with no cursor at the service's page size, the request
 * `ProjectActivityStore.fetch` makes today.
 */
export function projectActivitiesQuery(
  service: Pick<ProjectActivityService, "list">,
  scope: QueryScope,
  projectId: string,
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectActivitiesKey(scope, projectId),
    initialPageParam: null as string | null,
    queryFn: scopedRead<TProjectActivityPage, string | null>(
      (request, { pageParam }) =>
        service.list(scope.tenant, projectId, { cursor: pageParam }, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: TProjectActivityPage, _all, cursor) => nextFeedCursor(last, cursor),
  });
}

/** R4: the project's comments, newest first, one page per fetch. */
export function projectCommentsQuery(
  service: Pick<ProjectCommentService, "list">,
  scope: QueryScope,
  projectId: string,
) {
  return infiniteQueryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectCommentsKey(scope, projectId),
    initialPageParam: null as string | null,
    queryFn: scopedRead<TProjectCommentPage, string | null>(
      (request, { pageParam }) =>
        service.list(scope.tenant, projectId, { cursor: pageParam }, request),
      { retry: "safe-read" },
    ),
    getNextPageParam: (last: TProjectCommentPage, _all, cursor) => nextFeedCursor(last, cursor),
  });
}

/** R5: the project's attachments. */
export function projectAttachmentsQuery(
  service: Pick<ProjectService, "listAttachments">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectAttachmentsKey(scope, projectId),
    queryFn: scopedRead((request) => service.listAttachments(scope.tenant, projectId, request), {
      retry: "safe-read",
    }),
  });
}

/** R6: the intake requests linked to the project. */
export function projectLinkedSubmissionsQuery(
  service: Pick<ProjectLinkedSubmissionsService, "list">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectLinkedSubmissionsKey(scope, projectId),
    queryFn: scopedRead((request) => service.list(scope.tenant, projectId, request), {
      retry: "safe-read",
    }),
  });
}

/** R7: logged time per work item in the project. */
export function projectWorklogSummaryQuery(
  service: Pick<WorklogService, "getProjectWorklogSummary">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectWorklogSummaryKey(scope, projectId),
    queryFn: scopedRead(
      (request) => service.getProjectWorklogSummary(scope.tenant, projectId, request),
      { retry: "safe-read" },
    ),
  });
}

/** R8: the project's cost and estimate figures. */
export function projectCostSummaryQuery(
  service: Pick<WorklogService, "getProjectCostSummary">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectCostSummaryKey(scope, projectId),
    queryFn: scopedRead(
      (request) => service.getProjectCostSummary(scope.tenant, projectId, request),
      { retry: "safe-read" },
    ),
  });
}

/**
 * R9: the project's members, optionally narrowed by `q`. The one owner of the
 * roster: the members settings page and the work-items view both read it, each
 * shaping the rows it needs with `select`.
 */
export function projectMembersQuery(
  service: Pick<ProjectMemberService, "listMembers">,
  scope: QueryScope,
  projectId: string,
  { q }: { q?: string } = {},
) {
  const query = q?.trim() ?? "";
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: projectMembersKey(scope, projectId, { q: query }),
    queryFn: scopedRead(
      (request) => service.listMembers(scope.tenant, projectId, { q: query }, request),
      { retry: "safe-read" },
    ),
  });
}

/** R10: the signed-in user's own membership of the project. */
export function projectMemberMeQuery(
  service: Pick<ProjectMemberService, "memberMe">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: projectMemberMeKey(scope, projectId),
    queryFn: scopedRead((request) => service.memberMe(scope.tenant, projectId, request), {
      retry: "safe-read",
    }),
  });
}

/** R12: the signed-in user's work-item display properties for the project. */
export function projectUserPropertiesQuery(
  service: Pick<WorkItemService, "getDisplayProperties">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectUserPropertiesKey(scope, projectId),
    queryFn: scopedRead(
      (request) => service.getDisplayProperties(scope.tenant, projectId, request),
      { retry: "safe-read" },
    ),
  });
}

/** R13: Smart Assignment recommendations for each job role with work on the project. */
export function projectJobRoleRecommendationsQuery(
  service: Pick<WorkspaceJobRoleService, "recommendations">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectJobRoleRecommendationsKey(scope, projectId),
    queryFn: scopedRead((request) => service.recommendations(scope.tenant, projectId, request), {
      retry: "safe-read",
    }),
  });
}
